"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Cpu, Layers, Activity, Gauge } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface TechnologyItem {
  id: string;
  title: string;
  category: string;
  description: string;
  href: string;
  iconName: string;
}
const technologies: TechnologyItem[] = [{
  id: "tech-1",
  title: "Bruker Process Spectroscopy",
  category: "FT-NIR & Process Control",
  description: "High-performance optical spectrometers engineered for continuous process monitoring and quality verification in demanding production environments.",
  href: "/ProductsPage",
  iconName: "cpu"
}, {
  id: "tech-2",
  title: "Raman Spectroscopy Systems",
  category: "Molecular Identification",
  description: "Non-destructive molecular analysis solutions providing rapid chemical identification and raw material verification for regulated laboratory workflows.",
  href: "/ProductsPage",
  iconName: "layers"
}, {
  id: "tech-3",
  title: "In-Line NIR Systems",
  category: "Real-Time Analysis",
  description: "Robust near-infrared analytical instrumentation for real-time monitoring of blend uniformity, moisture content, and chemical composition.",
  href: "/ProductsPage",
  iconName: "activity"
}, {
  id: "tech-4",
  title: "VIAVI PAT Systems",
  category: "Process Analytical Technology",
  description: "Compact, solid-state optical sensing solutions designed for pharmaceutical manufacturing, drying monitoring, and automated process control.",
  href: "/ProductsPage",
  iconName: "gauge"
}];
function getTechIcon(name: string) {
  switch (name) {
    case "cpu":
      return <Cpu className="size-6 text-primary" aria-hidden="true" />;
    case "layers":
      return <Layers className="size-6 text-primary" aria-hidden="true" />;
    case "activity":
      return <Activity className="size-6 text-primary" aria-hidden="true" />;
    case "gauge":
      return <Gauge className="size-6 text-primary" aria-hidden="true" />;
    default:
      return <Cpu className="size-6 text-primary" aria-hidden="true" />;
  }
}
export default function TechnologyShowcase() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="technologyshowcase" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border/60">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
            Analytical Instrumentation
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-5">
            Application-Led Technology Portfolio
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Discover precision spectroscopic systems and process analytical
            technologies configured for regulated manufacturing and laboratory
            environments.
          </p>
        </div>

        {/* 4-Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {technologies.map((tech, i) => <motion.div key={tech.id} data-index={i} initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: shouldReduceMotion ? 0.2 : 0.45,
          delay: shouldReduceMotion ? 0 : i * 0.1,
          ease: "easeOut"
        }} className="h-full">
              <Card className="h-full flex flex-col justify-between border-border hover:border-primary/40 bg-card transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md">
                <div>
                  <CardHeader className="space-y-4">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center border border-border">
                      {getTechIcon(tech.iconName)}
                    </div>
                    <div>
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {tech.category}
                      </span>
                      <CardTitle className="text-xl font-default font-semibold text-card-foreground mt-1.5 leading-snug">
                        {tech.title}
                      </CardTitle>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {tech.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 mt-auto">
                  <Button asChild variant="ghost" className="w-full justify-between px-0 font-medium text-foreground hover:text-primary hover:bg-transparent group">
                    <Link to={tech.href} className="inline-flex items-center gap-2">
                      <span>Learn More</span>
                      <ArrowRight className="size-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Support Banner */}
        <div className="mt-14 p-6 sm:p-8 rounded-xl bg-secondary/70 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Need validation and qualification support?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our team provides application consulting, installation, and lifecycle
              services across Ireland and the UK.
            </p>
          </div>
          <Button asChild variant="default" className="shrink-0">
            <Link to="/ContactPage">Consult an Expert</Link>
          </Button>
        </div>
      </div>
    </section>;
}